import {
  Package, BookOpen, MessageSquareQuote, HelpCircle, Sparkles, Eye, Home,
  Layers, Palette, Drama, Wrench, Baby, ListChecks, AlertTriangle,
} from "lucide-react";

function Bi({ en, es }) {
  return (
    <div className="space-y-1">
      {en && en.trim() && <p className="text-foreground leading-relaxed">{en}</p>}
      {es && es.trim() && <p className="text-accent italic leading-relaxed">{es}</p>}
    </div>
  );
}

function Section({ icon: Icon, title, children }) {
  return (
    <section className="space-y-3">
      <div className="flex items-center gap-2">
        <span className="h-8 w-8 rounded-lg bg-muted flex items-center justify-center shrink-0">
          <Icon className="h-4 w-4 text-primary" strokeWidth={2} />
        </span>
        <h3 className="font-heading font-bold text-lg">{title}</h3>
      </div>
      <div className="pl-10">{children}</div>
    </section>
  );
}

function PairList({ items }) {
  return (
    <ul className="space-y-3">
      {(items || []).map((q, i) => <li key={i}><Bi en={q.en} es={q.es} /></li>)}
    </ul>
  );
}

function VocabChips({ vocabulary }) {
  return (
    <div className="flex flex-wrap gap-2">
      {(vocabulary || []).map((v, i) => (
        <div key={i} className="rounded-xl border border-border bg-card px-3 py-2">
          <span className="font-semibold text-foreground">{v.en}</span>
          {v.en && v.es && <span className="text-muted-foreground mx-2">—</span>}
          <span className="text-accent italic">{v.es}</span>
        </div>
      ))}
    </div>
  );
}

function Disclaimer({ t }) {
  return (
    <div className="flex items-start gap-2 rounded-xl bg-muted p-4 text-sm text-muted-foreground">
      <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0 text-secondary" />
      <p>{t("activity.disclaimer")}</p>
    </div>
  );
}

function Title({ en, es }) {
  return (
    <header className="space-y-1">
      <p className="text-xs font-semibold uppercase tracking-widest text-secondary hidden print:block">Brilla Bilingual Classroom Activity Coach</p>
      {en && <h2 className="font-heading font-extrabold text-3xl tracking-tight">{en}</h2>}
      {es && <p className="font-heading font-bold text-xl text-accent italic">{es}</p>}
    </header>
  );
}

export function CenterDisplay({ content: c, t }) {
  return (
    <article className="print-area space-y-8">
      <Title en={`${c.center_en || ""} · ${c.title_en || ""}`} es={`${c.center_es || ""} · ${c.title_es || ""}`} />
      {(c.setup_en || c.setup_es) && <Section icon={Wrench} title="Setup / Preparación"><Bi en={c.setup_en} es={c.setup_es} /></Section>}
      {(c.materials_en?.length || c.materials_es?.length) && (
        <Section icon={Package} title="Materials / Materiales">
          <div className="grid sm:grid-cols-2 gap-x-8 gap-y-1">
            <ul className="list-disc pl-5 space-y-1">{(c.materials_en || []).map((m, i) => <li key={i}>{m}</li>)}</ul>
            <ul className="list-disc pl-5 space-y-1">{(c.materials_es || []).map((m, i) => <li key={i} className="text-accent italic">{m}</li>)}</ul>
          </div>
        </Section>
      )}
      {c.vocabulary?.length > 0 && <Section icon={BookOpen} title="Vocabulary / Vocabulario"><VocabChips vocabulary={c.vocabulary} /></Section>}
      {c.teacher_prompts?.length > 0 && <Section icon={MessageSquareQuote} title="Teacher Prompts / Indicaciones"><PairList items={c.teacher_prompts} /></Section>}
      {c.questions?.length > 0 && <Section icon={HelpCircle} title="Questions / Preguntas"><PairList items={c.questions} /></Section>}
      {c.dll_strategies?.length > 0 && <Section icon={Sparkles} title="DLL Strategies / Estrategias DLL"><PairList items={c.dll_strategies} /></Section>}
      {(c.observations_en || c.observations_es) && <Section icon={Eye} title="Possible Observations / Observaciones"><Bi en={c.observations_en} es={c.observations_es} /></Section>}
      <Disclaimer t={t} />
    </article>
  );
}

export function FamilyDisplay({ content: c, t }) {
  return (
    <article className="print-area space-y-8">
      <Title en={`${c.title_en || "Let's Learn Together"}: ${c.activity_name_en || ""}`} es={`${c.title_es || "Aprendamos Juntos"}: ${c.activity_name_es || ""}`} />
      {(c.learning_en || c.learning_es) && <Section icon={Baby} title="What Your Child Is Learning / Lo que su niño está aprendiendo"><Bi en={c.learning_en} es={c.learning_es} /></Section>}
      {(c.try_en || c.try_es) && <Section icon={ListChecks} title="Try This at Home / Intente esto en casa"><Bi en={c.try_en} es={c.try_es} /></Section>}
      {c.words?.length > 0 && <Section icon={BookOpen} title="Words to Practice / Palabras para practicar"><VocabChips vocabulary={c.words} /></Section>}
      {c.questions?.length > 0 && <Section icon={HelpCircle} title="Questions to Ask / Preguntas para hacer"><PairList items={c.questions} /></Section>}
      {(c.materials_en?.length || c.materials_es?.length) && (
        <Section icon={Package} title="Materials / Materiales">
          <div className="grid sm:grid-cols-2 gap-x-8 gap-y-1">
            <ul className="list-disc pl-5 space-y-1">{(c.materials_en || []).map((m, i) => <li key={i}>{m}</li>)}</ul>
            <ul className="list-disc pl-5 space-y-1">{(c.materials_es || []).map((m, i) => <li key={i} className="text-accent italic">{m}</li>)}</ul>
          </div>
        </Section>
      )}
      <Disclaimer t={t} />
    </article>
  );
}

export function BookDisplay({ content: c, t }) {
  return (
    <article className="print-area space-y-8">
      <Title en={c.book_title} es={c.author ? `by ${c.author}` : ""} />
      {c.before?.length > 0 && <Section icon={HelpCircle} title="Before Reading / Antes de leer"><PairList items={c.before} /></Section>}
      {c.picture_walk?.length > 0 && <Section icon={Eye} title="Picture Walk / Paseo por las imágenes"><PairList items={c.picture_walk} /></Section>}
      {c.predictions?.length > 0 && <Section icon={Sparkles} title="Predictions / Predicciones"><PairList items={c.predictions} /></Section>}
      {c.during?.length > 0 && <Section icon={HelpCircle} title="During Reading / Durante la lectura"><PairList items={c.during} /></Section>}
      {c.after?.length > 0 && <Section icon={HelpCircle} title="After Reading / Después de leer"><PairList items={c.after} /></Section>}
      {c.recall?.length > 0 && <Section icon={ListChecks} title="Recall Questions / Preguntas de recuerdo"><PairList items={c.recall} /></Section>}
      {c.higher_order?.length > 0 && <Section icon={HelpCircle} title="Higher-Order Questions / Preguntas de orden superior"><PairList items={c.higher_order} /></Section>}
      {c.vocabulary?.length > 0 && <Section icon={BookOpen} title="Vocabulary / Vocabulario"><VocabChips vocabulary={c.vocabulary} /></Section>}
      {c.extensions?.length > 0 && <Section icon={ListChecks} title="Extension Activities / Actividades de extensión"><PairList items={c.extensions} /></Section>}
      {(c.art_connection_en || c.art_connection_es) && <Section icon={Palette} title="Art Connection / Conexión artística"><Bi en={c.art_connection_en} es={c.art_connection_es} /></Section>}
      {(c.dramatic_play_en || c.dramatic_play_es) && <Section icon={Drama} title="Dramatic Play / Juego dramático"><Bi en={c.dramatic_play_en} es={c.dramatic_play_es} /></Section>}
      {(c.family_en || c.family_es) && <Section icon={Home} title="Family Connection / Conexión familiar"><div className="rounded-2xl bg-secondary/10 border border-secondary/20 p-4"><Bi en={c.family_en} es={c.family_es} /></div></Section>}
      <Disclaimer t={t} />
    </article>
  );
}

export function WeeklyPlanDisplay({ content: c, t }) {
  return (
    <article className="print-area space-y-6">
      <Title en={`Weekly Plan: ${c.theme_en || ""}`} es={`Plan Semanal: ${c.theme_es || ""}`} />
      {(c.overview_en || c.overview_es) && (
        <div className="rounded-2xl bg-muted p-4"><Bi en={c.overview_en} es={c.overview_es} /></div>
      )}
      <div className="space-y-5">
        {(c.days || []).map((d, i) => (
          <div key={i} className="rounded-2xl border border-border overflow-hidden">
            <div className="bg-primary text-primary-foreground px-4 py-2">
              <h3 className="font-heading font-bold">{d.day_en}{d.day_es ? ` · ${d.day_es}` : ""}</h3>
            </div>
            <div className="p-4 grid sm:grid-cols-2 gap-3">
              {(d.blocks || []).map((b, j) => (
                <div key={j} className="rounded-xl border border-border p-3 space-y-1">
                  <p className="text-xs font-semibold uppercase tracking-wide text-secondary">{b.category_en}{b.category_es ? ` / ${b.category_es}` : ""}</p>
                  {b.title_en && <p className="font-semibold">{b.title_en}</p>}
                  {b.title_es && <p className="font-semibold text-accent italic">{b.title_es}</p>}
                  <Bi en={b.description_en} es={b.description_es} />
                  {b.vocabulary?.length > 0 && (
                    <p className="text-sm text-muted-foreground pt-1">
                      {b.vocabulary.map((v) => `${v.en} — ${v.es}`).join(" · ")}
                    </p>
                  )}
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
      <Disclaimer t={t} />
    </article>
  );
}
